import { Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AgentAvatar } from '@/components/AgentAvatar';
import { AgentCard } from '@/components/AgentCard';
import TopNav from '@/components/TopNav';
import { PageLayout, PageHeader } from '@/components/poof-ui';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyAgents, type AgentsResponse } from '@/lib/collections/agents';
import { isFunctionalAgentId } from '@/utils/agentContent';
import { formatSol } from '@/utils/format';

interface DropdownRect {
  top: number;
  left: number;
  width: number;
}

export const MarketplacePage: React.FC = () => {
  const { data: agents, loading } = useRealtimeData<AgentsResponse[]>(subscribeManyAgents, true, 'limit 50');
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rect, setRect] = useState<DropdownRect | null>(null);

  // wrapperRef anchors the whole search control (input + icon buttons) — used
  // both for click-outside detection and to measure where the portaled
  // dropdown should be positioned/sized.
  const wrapperRef = useRef<HTMLDivElement>(null);
  // dropdownRef points at the portaled menu itself (lives in document.body,
  // outside wrapperRef's subtree) so click-outside detection also excludes it.
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateRect = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 8, left: r.left, width: r.width });
  }, []);

  // Recompute the anchor rect as soon as the dropdown opens (before paint),
  // then keep it in sync on scroll/resize while it stays open.
  useLayoutEffect(() => {
    if (!dropdownOpen) return;
    updateRect();
  }, [dropdownOpen, updateRect]);

  useEffect(() => {
    if (!dropdownOpen) return;
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [dropdownOpen, updateRect]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sorted = (agents ?? [])
    .slice()
    .sort((a, b) => {
      const aSeeded = Number(isFunctionalAgentId(a.id));
      const bSeeded = Number(isFunctionalAgentId(b.id));
      if (aSeeded !== bSeeded) return bSeeded - aSeeded;
      const aFn = Number(a.isFunctional);
      const bFn = Number(b.isFunctional);
      if (aFn !== bFn) return bFn - aFn;
      return (b.createdAt ?? 0) - (a.createdAt ?? 0);
    });

  // Case-insensitive match on name, description, and capabilityTags.
  const searchMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return sorted
      .filter((a) => {
        const name = a.name?.toLowerCase() ?? '';
        const description = a.description?.toLowerCase() ?? '';
        const tags = a.capabilityTags?.toLowerCase() ?? '';
        return name.includes(q) || description.includes(q) || tags.includes(q);
      })
      .slice(0, 8);
  }, [sorted, query]);

  const goToAgent = (agentId: string) => {
    setDropdownOpen(false);
    setQuery('');
    navigate(`/agents/${agentId}`);
  };

  const showDropdown = dropdownOpen && query.trim().length > 0;

  return (
    <PageLayout background="" fullBleed footer={false}>
      <TopNav />
      <div className="container pb-24 lg:pb-10">
        <PageHeader title="Agent Marketplace" description="Browse and hire AI agents, paid per call in native SOL." />

        <div ref={wrapperRef} className="w-full sm:max-w-md mt-6">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Search agents by name, tag, or skill…"
              className="w-full rounded-full bg-card border border-border pl-10 pr-9 py-3 sm:py-2.5 text-sm outline-none transition-colors duration-200 focus:ring-2 focus:ring-primary/50 focus:border-primary/60"
            />
            {query && (
              <button
                onClick={() => {
                  setQuery('');
                  setDropdownOpen(false);
                }}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Portaled to document.body so the dropdown always escapes any
            backdrop-filter/overflow-hidden/transform stacking context created
            by ancestors (TopNav's glassmorphism bar, card surfaces, etc.) and
            can sit above everything with a single high z-index. Positioned
            via a measured rect rather than CSS so it tracks the input exactly
            on every screen size. */}
        {showDropdown &&
          rect &&
          createPortal(
            <div
              ref={dropdownRef}
              style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 9999 }}
              className="rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-fade-in"
            >
              {searchMatches.length === 0 ? (
                <p className="px-4 py-3.5 text-sm text-muted-foreground">No agents match “{query}”.</p>
              ) : (
                <ul className="max-h-[60vh] sm:max-h-80 overflow-y-auto divide-y divide-border">
                  {searchMatches.map((agent) => (
                    <li key={agent.id}>
                      <button
                        onClick={() => goToAgent(agent.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 sm:py-2.5 text-left transition-colors duration-150 hover:bg-secondary/70 active:bg-secondary"
                      >
                        <AgentAvatar
                          avatarUrl={agent.avatarUrl}
                          avatarGradient={agent.avatarGradient}
                          name={agent.name}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{agent.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{agent.description}</p>
                        </div>
                        <span className="text-xs font-bold text-primary shrink-0">{formatSol(agent.pricePerCall)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>,
            document.body
          )}

        {loading && <p className="text-sm text-muted-foreground mt-6">Loading agents…</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {sorted.map((agent, i) => (
            <div key={agent.id} className="animate-fade-in-up" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
              <AgentCard agent={agent} />
            </div>
          ))}
        </div>

        {!loading && sorted.length === 0 && (
          <p className="text-sm text-muted-foreground mt-6">No agents listed yet.</p>
        )}
      </div>
    </PageLayout>
  );
};

export default MarketplacePage;
